// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect } from 'react';
import styled from 'styled-components'
import { Telemetry } from 'telemetry/TelemetryManager';
import { useTelemetryContext } from 'hooks/TelemetryContext';
import { ExplorerPanel } from 'components/myContentExplorer/ExplorerPanel';
import { ExplorerCanvas } from 'components/myContentExplorer/ExplorerCanvas';
import { useExplorerNavigationGroups } from 'hooks/ExplorerNavigationGroups';
import { DocSourceId, ExplorerButtonId } from 'components/myContentExplorer/ExplorerTypes';
import { useFileCount } from 'filemanager/FileCountView';
import { useDeepLinkInitializer } from 'hooks/deepLinkInitializer';
import { useParams } from 'react-router';
import { NavigationType, useNavigationService } from 'hooks/navigationService';
import { usePageLimits } from 'hooks/PageLimits';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';

const DocExplorerPageWindow = styled.div`
    height: 100%;
    width: 100%;
    background: white;
    overflow: hidden;
`;

const ExplorerPanelContainer = styled.div`
    width: 18%;
    height: 100%;
    flex: 1;
    min-width: 250px;
`;

const ExplorerCanvasContainer = styled.div`
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 4.5;
    height: 100%;
`;

const ExplorerContentContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
`;

export const MyContentLandingPage = (): JSX.Element => {
    const targetId = useParams<{targetId?: string}>().targetId;
    const navService = useNavigationService();
    const {pageUploadDisabled: isAddPagesLimited} = usePageLimits();

    useDeepLinkInitializer();

    useEffect(() => {
        if (isAddPagesLimited) {
            if (targetId === ExplorerButtonId.AddDocs) {
                // Adding docs is limited, redirect the user to their local docs
                navService.navToLocalDocs(NavigationType.ReplaceCurrentPage);
            }
        }
    }, [isAddPagesLimited, targetId, navService])

    const fileCount = useFileCount();
    const defaultId = fileCount === 0 && !isAddPagesLimited ? ExplorerButtonId.AddDocs : DocSourceId.Docugami;

    // Create our navigation groups
    const groups = useExplorerNavigationGroups(() => {}, defaultId, targetId);

    useTelemetryContext({ explorerNavigationGroup: groups.selectedGroup, explorerNavigationId: targetId });

    useEffect(() => {
        Telemetry.trackPageView(`${TelemetryPageView.DocExplorer}/${groups.selectedGroup ?? ''}`, { id: targetId ?? '' });
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetId]);

    return (
        <DocExplorerPageWindow>
            <ExplorerContentContainer>
                <ExplorerPanelContainer>
                    <ExplorerPanel selectedId={targetId} groups={groups.items} />
                </ExplorerPanelContainer>
                <ExplorerCanvasContainer>
                    <ExplorerCanvas group={groups.selectedGroup} itemId={targetId} type={groups.selectedType} />
                </ExplorerCanvasContainer>
            </ExplorerContentContainer>
        </DocExplorerPageWindow>
    )
}